import React, { useEffect, useState } from "react";
import { Flex } from "@fluentui/react-northstar";
import { CompanyReportSectionConfig, Scope, SelectionTypeEnum } from "../models";
import { ReportSection } from "../ReportSection";
import { useQueryClient } from "react-query";
import { ModesFilter, SegmentsFilter, YearsFilter } from "../filters";
import { SalesSubSection } from "./SalesSubSection";
import { ActivitySubSection } from "./ActivitySubSection";
import pl from "shared/messages/pl";
import { getThemeMainColor } from "msTeamsUI/chartThemes/chartThemes";

export enum SalesAndActivitySectionDataSource {
    salesPerson,
    intermediary,
}

export interface SalesAndActivitySectionProps {
    dataSource: SalesAndActivitySectionDataSource;
    id: number;
    salesPersonIntermediaryNumber?: string;
    scope?: Scope;
    isMobileView: boolean;
    sectionConfig: CompanyReportSectionConfig;
}

export const SalesAndActivitySection: React.FC<SalesAndActivitySectionProps> = (props) => {
    const queryClient = useQueryClient();

    const [years, setYears] = useState(props.sectionConfig.periodDefinition.yearList);
    const [segments, setSegments] = useState(props.sectionConfig.company.segmentDefinition?.segmentList ?? []);
    const [modes, setModes] = useState(props.sectionConfig.modeList);

    useEffect(() => {
        const init = async () => {
            const salesQueryKeys = `sales_${props.sectionConfig.company.code}`;
            const activityQueryKeys = `activity_${props.sectionConfig.company.code}`;
            const queryKeys = [salesQueryKeys, activityQueryKeys];
            await Promise.all(queryKeys.map((x) => queryClient.invalidateQueries(x)));
        };

        init();
    }, [props.scope?.code]);

    const getFilters = () => {
        const result: any = [];
        result.push(
            <SegmentsFilter
                key="segments"
                segmentList={segments}
                onSelectedSegmentsChanged={(segmentList) => setSegments(segmentList)}
                isMobileView={props.isMobileView}
                atLeastOneAlwaysSelected
                multiple={props.sectionConfig.company.segmentDefinition?.selectionType === SelectionTypeEnum.MULTIPLE}
                prefix={props.sectionConfig.company.name}
            />
        );
        result.push(
            <YearsFilter
                key={"years-filter"}
                years={years}
                onSelectedYearsChanged={(years) => setYears(years)}
                isMobileView={props.isMobileView}
                multiple={props.sectionConfig.periodDefinition.selectionType === SelectionTypeEnum.MULTIPLE}
                atLeastOneAlwaysSelected={props.sectionConfig.periodDefinition.isRequired}
            />
        );
        result.push(
            <ModesFilter
                key={"modes-filter"}
                modes={modes}
                onSelectedModesChanged={(modes) => setModes(modes)}
                isMobileView={props.isMobileView}
            />
        );
        return result;
    };

    const content = (
        <Flex column gap="gap.large">
            <SalesSubSection
                dataSource={props.dataSource}
                id={props.id}
                salesPersonIntermediaryNumber={props.salesPersonIntermediaryNumber}
                scope={props.scope}
                company={props.sectionConfig.company}
                selectedSegments={segments.filter((segment) => segment.selected).map((segment) => segment.code)}
                selectedYears={years.filter((year) => year.selected).map((year) => year.code)}
                selectedMode={modes.find((mode) => mode.selected)?.code ?? ""}
            />
            <ActivitySubSection
                dataSource={props.dataSource}
                id={props.id}
                salesPersonIntermediaryNumber={props.salesPersonIntermediaryNumber}
                scope={props.scope}
                company={props.sectionConfig.company}
                selectedSegments={segments.filter((segment) => segment.selected).map((segment) => segment.code)}
                selectedYears={years.filter((year) => year.selected).map((year) => year.code)}
                selectedMode={modes.find((mode) => mode.selected)?.code ?? ""}
            />
        </Flex>
    );

    return (
        <ReportSection
            title={`${pl.view360Card.reports.salesAndActivitySectionTitle} ${props.sectionConfig.company.name}`}
            filters={getFilters()}
            reportContent={content}
            isMobileView={props.isMobileView}
            isCollapsible={props.isMobileView}
            highlightColor={getThemeMainColor(props.sectionConfig.company.themeName)}
        />
    );
};
